import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import {
  CustomBodyCell,
  CustomHeaderCell,
  CustomTable,
  CustomTableContainer,
  CustomTableRow,
} from '@components/Styles/TableStyle';
import { CustomStack, RequiredMark, RequiredMarkBox } from '@components/Styles/CommonStyle';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import {
  PROFILE_EDUCATION_STATUS_OPTIONS,
  PROFILE_GENDER_OPTIONS,
  PROFILE_GRADE_OPTIONS,
  PROFILE_WORK_TYPE_OPTIONS,
} from '@constants/profile';
import React, { useMemo } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ControlledAutocompleteBox from '@components/common/Input/ControlledAutocompleteBox';
import ControlledCheckBox from '@components/common/Input/ControlledCheckBox';
import ControlledRadioBox from '@components/common/Input/ControlledRadioBox';
import ControlledSelectBox from '@components/common/Input/ControlledSelectBox';
import ControlledTextInput from '@components/common/Input/ControlledTextInput';
import { CustomButton } from '@components/Styles/ButtonStyle';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { SectionWrapper } from '@components/Styles/CommonStyle';
import { Typography } from '@mui/material';
import { defaultProfileValues } from '@containers/profile/ProfileCreate';
import { tableStyles } from '@components/Styles/TableStyle';
import { theme } from '@components/Styles/theme';
import { useDialog } from '@context/DialogContext';

// 메모이제이션된 행 컴포넌트로 리렌더링 최적화
const WorkExperienceRow = React.memo(({ item, index, control, removeWorkExperience, providedDraggable, snapshot }) => (
  <TableRow
    key={item.id}
    ref={providedDraggable.innerRef}
    {...providedDraggable.draggableProps}
    sx={{
      ...(snapshot.isDragging && { backgroundColor: theme.palette.action.hover }),
    }}
  >
    <TableCell sx={{ ...tableStyles.commonTable.cell }} align="center" width="60px">
      <Box {...providedDraggable.dragHandleProps} sx={{ display: 'inline-flex', cursor: 'grab' }}>
        <DragIndicatorIcon />
      </Box>
    </TableCell>
    <TableCell sx={tableStyles.commonTable.cell}>
      <ControlledTextInput key={`we-company-${item.id}`} name={`workExperienceList[${index}].companyName`} />
    </TableCell>
    <TableCell sx={tableStyles.commonTable.cell}>
      <CustomStack>
        <ControlledTextInput
          key={`we-start-${item.id}`}
          name={`workExperienceList[${index}].startDate`}
          maxLength={7}
          valueType="date"
          placeholder="YYYY.MM"
        />
        <span>~</span>
        <ControlledTextInput
          key={`we-end-${item.id}`}
          name={`workExperienceList[${index}].endDate`}
          maxLength={7}
          valueType="date"
          placeholder="YYYY.MM"
        />
      </CustomStack>
    </TableCell>
    <TableCell sx={tableStyles.commonTable.cell}>
      <ControlledTextInput key={`we-position-${item.id}`} name={`workExperienceList[${index}].position`} />
    </TableCell>
    <TableCell sx={tableStyles.commonTable.cell}>
      <ControlledTextInput key={`we-duty-${item.id}`} name={`workExperienceList[${index}].duty`} />
    </TableCell>
    <TableCell sx={tableStyles.commonTable.cell} align="center">
      <CustomButton size="small" color="error" onClick={() => removeWorkExperience(index)}>
        행삭제
      </CustomButton>
    </TableCell>
  </TableRow>
));

// 프로젝트 이력을 위한 메모이제이션된 행 컴포넌트
const ProjectHistoryRow = React.memo(({ item, index, control, removeProjectHistory, providedDraggable, snapshot }) => (
  <TableRow
    key={item.id}
    ref={providedDraggable.innerRef}
    {...providedDraggable.draggableProps}
    sx={{
      ...(snapshot.isDragging && { backgroundColor: theme.palette.action.hover }),
    }}
  >
    <TableCell sx={{ ...tableStyles.commonTable.cell }} align="center" width="60px">
      <Box {...providedDraggable.dragHandleProps} sx={{ display: 'inline-flex', cursor: 'grab' }}>
        <DragIndicatorIcon />
      </Box>
    </TableCell>
    <TableCell sx={tableStyles.commonTable.cell}>
      <ControlledTextInput key={`ph-name-${item.id}`} name={`projectHistoryList[${index}].projectName`} />
    </TableCell>
    <TableCell sx={tableStyles.commonTable.cell}>
      <CustomStack>
        <ControlledTextInput
          key={`ph-start-${item.id}`}
          name={`projectHistoryList[${index}].startDate`}
          maxLength={7}
          valueType="date"
          placeholder="YYYY.MM"
        />
        <span>~</span>
        <ControlledTextInput
          key={`ph-end-${item.id}`}
          name={`projectHistoryList[${index}].endDate`}
          maxLength={7}
          valueType="date"
          placeholder="YYYY.MM"
        />
      </CustomStack>
    </TableCell>
    <TableCell sx={tableStyles.commonTable.cell}>
      <ControlledTextInput key={`ph-client-${item.id}`} name={`projectHistoryList[${index}].clientCompany`} />
    </TableCell>
    <TableCell sx={tableStyles.commonTable.cell}>
      <ControlledTextInput key={`ph-employer-${item.id}`} name={`projectHistoryList[${index}].employerCompany`} />
    </TableCell>
    <TableCell sx={tableStyles.commonTable.cell}>
      <ControlledTextInput key={`ph-duty-${item.id}`} name={`projectHistoryList[${index}].duty`} />
    </TableCell>
    <TableCell sx={tableStyles.commonTable.cell}>
      <ControlledTextInput key={`ph-skill-${item.id}`} name={`projectHistoryList[${index}].skill`} />
    </TableCell>
    <TableCell sx={tableStyles.commonTable.cell} align="center">
      <CustomButton size="small" color="error" onClick={() => removeProjectHistory(index)}>
        행삭제
      </CustomButton>
    </TableCell>
  </TableRow>
));

const ProfileFormTable = ({ initialData, onSubmit, onCancel }) => {
  const { showDialog } = useDialog();

  // 기본값을 설정하는 함수
  const setDefaultValues = (data) => {
    const educationList = [
      ...(data.educationList || []),
      ...Array.from({ length: Math.max(0, 3 - (data.educationList || []).length) }, () => ({
        ...defaultProfileValues.educationList[0],
      })),
    ];
    return {
      ...defaultProfileValues,
      ...data,
      educationList,
      workExperienceList:
        data.workExperienceList && data.workExperienceList.length > 0
          ? data.workExperienceList
          : defaultProfileValues.workExperienceList,
      projectHistoryList:
        data.projectHistoryList && data.projectHistoryList.length > 0
          ? data.projectHistoryList
          : defaultProfileValues.projectHistoryList,
      profileData: {
        ...defaultProfileValues.profileData,
        ...(data.profileData || {}),
      },
    };
  };

  const handleErrors = (errors) => {
    showDialog({ title: '확인', message: '필수 입력 항목이 누락되었습니다.\n확인해 주세요.' });
  };

  // useForm에 설정 추가
  const methods = useForm({
    defaultValues: setDefaultValues(initialData),
    shouldUnregister: false, // 필드 제거 시에도 값 유지
    mode: 'onChange', // 입력 시 유효성 검사
  });

  const { control, handleSubmit, getValues, setValue } = methods;

  const { fields: educationFields } = useFieldArray({
    control,
    name: 'educationList',
  });

  // 빈 객체는 메모이제이션
  const emptyWorkExperience = useMemo(
    () => ({
      companyName: '',
      startDate: '',
      endDate: '',
      position: '',
      duty: '',
    }),
    [],
  );

  const emptyProjectHistory = useMemo(
    () => ({
      projectName: '',
      startDate: '',
      endDate: '',
      clientCompany: '',
      employerCompany: '',
      duty: '',
      skill: '',
    }),
    [],
  );

  // prepend와 함께 update 메서드도 가져오기
  const {
    fields: workExperienceFields,
    prepend: prependWorkExperience,
    update: updateWorkExperience,
    remove: removeWorkExperience,
    move: moveWorkExperience,
  } = useFieldArray({
    control,
    name: 'workExperienceList',
  });

  const {
    fields: projectHistoryFields,
    prepend: prependProjectHistory,
    update: updateProjectHistory,
    remove: removeProjectHistory,
    move: moveProjectHistory,
  } = useFieldArray({
    control,
    name: 'projectHistoryList',
  });

  // 근무경력 행 추가 시
  const handleAddWorkExperience = () => {
    prependWorkExperience(emptyWorkExperience);
    setTimeout(() => {
      const values = getValues('workExperienceList');
      values.forEach((item, idx) => {
        updateWorkExperience(idx, item);
      });
    }, 0);
  };

  // 근무경력 드래그 종료 핸들러
  const handleDragEndWorkExperience = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    moveWorkExperience(result.source.index, result.destination.index);

    // move 이후 update 로직 추가
    setTimeout(() => {
      const values = getValues('workExperienceList');
      // update는 개별 필드에 대해 수행하거나, 전체 필드 배열을 재설정할 수도 있습니다.
      // 여기서는 각 항목을 순회하며 update를 시도합니다.
      // react-hook-form v7 이상에서는 전체 배열을 setValue로 덮어쓰는 것이 더 안정적일 수 있습니다.
      // 하지만 우선 update를 시도해봅니다.
      values.forEach((item, idx) => {
        updateWorkExperience(idx, item);
      });
      // setValue('workExperienceList', values, { shouldDirty: true, shouldFocus: false }); // 대체 방안
    }, 0);
  };

  // 프로젝트 이력 행 추가 시 전체 업데이트
  const handleAddProjectHistory = () => {
    prependProjectHistory(emptyProjectHistory);
    setTimeout(() => {
      const values = getValues('projectHistoryList');
      values.forEach((item, idx) => {
        updateProjectHistory(idx, item);
      });
    }, 0);
  };

  // 프로젝트 이력 드래그 종료 핸들러
  const handleDragEndProjectHistory = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    moveProjectHistory(result.source.index, result.destination.index);

    // move 이후 update 로직 추가
    setTimeout(() => {
      const values = getValues('projectHistoryList');
      values.forEach((item, idx) => {
        updateProjectHistory(idx, item);
      });
    }, 0);
  };

  const profileColumns = [
    { label: '성명', name: 'profileData.name', isRequired: true, maxLength: 20 },
    {
      label: '생년월일',
      name: 'profileData.birthDate',
      isRequired: true,
      maxLength: 8,
      valueType: 'number',
      placeholder: 'YYYYMMDD',
    },
    { label: '성별', name: 'profileData.gender', type: 'select', options: PROFILE_GENDER_OPTIONS, isRequired: true },
    { label: '경력', name: 'profileData.workPeriod', isRequired: true },
    { label: '등급', name: 'profileData.grade', type: 'select', options: PROFILE_GRADE_OPTIONS, isRequired: true },
    { label: '근무구분', name: 'profileData.workType', type: 'select', options: PROFILE_WORK_TYPE_OPTIONS },
    { label: '휴대폰', name: 'profileData.phone', isRequired: true, valueType: 'number', maxLength: 12 },
    { label: '이메일', name: 'profileData.email', isRequired: true },
    { label: '직위', name: 'profileData.position' },
  ];

  const educationColumns = ['학교', '전공', '졸업여부', '졸업년도', '졸업월'];
  const workExperienceColumns = ['정렬', '회사명', '근무기간', '직위', '담당업무', '관리'];
  const projectHistoryColumns = ['정렬', '사업명', '참여기간', '고객사', '근무회사', '역할', '사용기술', '관리'];

  const renderRowType = (column) => {
    switch (column.type) {
      case 'select':
        return <ControlledSelectBox name={column.name} options={column.options} isRequired={column.isRequired} />;
      case 'radio':
        return <ControlledRadioBox name={column.name} options={column.options} isRequired={column.isRequired} />;
      case 'checkbox':
        return <ControlledCheckBox name={column.name} label={column.label} isRequired={column.isRequired} />;
      default:
        return (
          <ControlledTextInput
            name={column.name}
            maxLength={column.maxLength || null}
            isRequired={column.isRequired}
            valueType={column.valueType || null}
            placeholder={column.placeholder || null}
          />
        );
    }
  };

  const renderTableHeader = (columns) => (
    <TableHead>
      <CustomTableRow>
        {columns.map((column, index) => (
          <CustomHeaderCell align="center" key={index}>
            {column}
          </CustomHeaderCell>
        ))}
      </CustomTableRow>
    </TableHead>
  );

  const renderProfileRows = (profileColumns) => {
    return profileColumns
      .reduce((acc, column, index) => {
        const colIndex = index % 3;
        const cell = (
          <React.Fragment key={index}>
            <CustomHeaderCell colSpan={1}>
              {column.isRequired && <RequiredMark />}
              {column.label}
            </CustomHeaderCell>
            <CustomBodyCell colSpan={1}>{renderRowType(column)}</CustomBodyCell>
          </React.Fragment>
        );

        if (colIndex === 0) {
          acc.push([]);
        }

        acc[acc.length - 1].push(cell);
        return acc;
      }, [])
      .map((row, rowIndex) => <CustomTableRow key={rowIndex}>{row}</CustomTableRow>);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, handleErrors)} autoComplete="off">
        <SectionWrapper>
          <CustomStack justifyContent="spaceBetween" mb={2}>
            <Typography sx={theme.typography.h4}>정보</Typography>
            <RequiredMarkBox>
              <RequiredMark />
              (&nbsp;&nbsp;) 필수입력사항
            </RequiredMarkBox>
          </CustomStack>
          <CustomTableContainer>
            <CustomTable sixColumn>
              <TableBody>
                {renderProfileRows(profileColumns)}
                <CustomTableRow>
                  <CustomHeaderCell colSpan={1}>주소</CustomHeaderCell>
                  <CustomBodyCell colSpan={5}>
                    <ControlledTextInput name="profileData.address" isFullWidth />
                  </CustomBodyCell>
                </CustomTableRow>
                <CustomTableRow>
                  <CustomHeaderCell colSpan={1}>자격증</CustomHeaderCell>
                  <CustomBodyCell colSpan={2}>
                    <ControlledTextInput name="profileData.license" isFullWidth />
                  </CustomBodyCell>
                  <CustomHeaderCell colSpan={1}>취득년월</CustomHeaderCell>
                  <CustomBodyCell colSpan={2}>
                    <CustomStack>
                      <ControlledTextInput
                        name="profileData.acquisitionYear"
                        maxLength={4}
                        valueType="number"
                        placeholder="YYYY"
                      />
                      <ControlledTextInput
                        name="profileData.acquisitionMonth"
                        maxLength={2}
                        valueType="number"
                        placeholder="MM"
                      />
                    </CustomStack>
                  </CustomBodyCell>
                </CustomTableRow>
              </TableBody>
            </CustomTable>
          </CustomTableContainer>
        </SectionWrapper>

        <SectionWrapper>
          <CustomStack mb={2}>
            <Typography sx={theme.typography.h4}>학력</Typography>
          </CustomStack>
          <CustomTableContainer>
            <CustomTable>
              {renderTableHeader(educationColumns)}
              <TableBody>
                {educationFields.map((item, index) => (
                  <CustomTableRow key={item.id}>
                    <CustomBodyCell align="center">
                      <ControlledTextInput key={`edu-school-${item.id}`} name={`educationList[${index}].schoolName`} />
                    </CustomBodyCell>
                    <CustomBodyCell align="center">
                      <ControlledTextInput key={`edu-major-${item.id}`} name={`educationList[${index}].major`} />
                    </CustomBodyCell>
                    <CustomBodyCell align="center">
                      <ControlledRadioBox
                        key={`edu-status-${item.id}`}
                        name={`educationList[${index}].educationStatus`}
                        options={PROFILE_EDUCATION_STATUS_OPTIONS}
                      />
                    </CustomBodyCell>
                    <CustomBodyCell align="center">
                      <ControlledTextInput
                        key={`edu-year-${item.id}`}
                        name={`educationList[${index}].graduationYear`}
                        maxLength={4}
                        valueType="number"
                        placeholder="YYYY"
                      />
                    </CustomBodyCell>
                    <CustomBodyCell align="center">
                      <ControlledTextInput
                        key={`edu-month-${item.id}`}
                        name={`educationList[${index}].graduationMonth`}
                        maxLength={2}
                        valueType="number"
                        placeholder="MM"
                      />
                    </CustomBodyCell>
                  </CustomTableRow>
                ))}
              </TableBody>
            </CustomTable>
          </CustomTableContainer>
        </SectionWrapper>

        <SectionWrapper>
          <CustomStack justifyContent="spaceBetween" mb={2}>
            <Typography sx={theme.typography.h4}>근무경력</Typography>
            <CustomButton size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddWorkExperience}>
              행추가
            </CustomButton>
          </CustomStack>
          <CustomTableContainer>
            <DragDropContext onDragEnd={handleDragEndWorkExperience}>
              <Table>
                {renderTableHeader(workExperienceColumns)}
                <Droppable droppableId="work-experience-droppable">
                  {(providedDroppable) => (
                    <TableBody ref={providedDroppable.innerRef} {...providedDroppable.droppableProps}>
                      {workExperienceFields.map((item, index) => (
                        <Draggable key={item.id} draggableId={item.id} index={index}>
                          {(providedDraggable, snapshot) => (
                            <WorkExperienceRow
                              item={item}
                              index={index}
                              control={control}
                              removeWorkExperience={removeWorkExperience}
                              providedDraggable={providedDraggable}
                              snapshot={snapshot}
                            />
                          )}
                        </Draggable>
                      ))}
                      {providedDroppable.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </Table>
            </DragDropContext>
          </CustomTableContainer>
        </SectionWrapper>

        <SectionWrapper>
          <CustomStack justifyContent="spaceBetween" mb={2}>
            <Typography sx={theme.typography.h4}>프로젝트 이력</Typography>
            <CustomButton size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddProjectHistory}>
              행추가
            </CustomButton>
          </CustomStack>
          <CustomTableContainer>
            <DragDropContext onDragEnd={handleDragEndProjectHistory}>
              <Table>
                {renderTableHeader(projectHistoryColumns)}
                <Droppable droppableId="project-history-droppable">
                  {(providedDroppable) => (
                    <TableBody ref={providedDroppable.innerRef} {...providedDroppable.droppableProps}>
                      {projectHistoryFields.map((item, index) => (
                        <Draggable key={item.id} draggableId={item.id} index={index}>
                          {(providedDraggable, snapshot) => (
                            <ProjectHistoryRow
                              item={item}
                              index={index}
                              control={control}
                              removeProjectHistory={removeProjectHistory}
                              providedDraggable={providedDraggable}
                              snapshot={snapshot}
                            />
                          )}
                        </Draggable>
                      ))}
                      {providedDroppable.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </Table>
            </DragDropContext>
          </CustomTableContainer>
        </SectionWrapper>

        <CustomStack justifyContent="center" mt={4}>
          <CustomButton color="gray" onClick={onCancel}>
            취소
          </CustomButton>
          <CustomButton onClick={handleSubmit(onSubmit, handleErrors)}>저장</CustomButton>
        </CustomStack>
      </form>
    </FormProvider>
  );
};

export default ProfileFormTable;
